import { MapPin, Briefcase, Star, ExternalLink } from "lucide-react";
import { Card } from "@/components/ui/card";

/**
 * Phase-3 UI blocks — powered by the Phase-3 HESA enrichment
 * (NSS 2025 themes, employment activity 15mo post-grad,
 * institution address/postcode, KIS course meta).
 *
 * Every block fails silently (returns null) when its slice of
 * `hesa_stats` isn't populated.
 */

const HESA_ATTR = "HESA Discover Uni 2024/25 · CC-BY-4.0";

/* ─── Section 5/10 — NSS 2025 themes bar chart ────────────────────────── */

export function NssThemesBlock({ nss }) {
  if (!nss?.themes) return null;
  const themes = Object.entries(nss.themes);
  if (themes.length === 0) return null;
  return (
    <div data-testid="nss-themes" className="mt-5 rounded-lg border border-stone-200 bg-white p-4">
      <div className="label-uppercase text-stone-600 mb-3 flex items-center gap-1.5">
        <Star className="h-3 w-3" /> Student satisfaction · NSS 2025
        <span className="text-[9px] font-mono text-stone-400 ml-auto">
          n={nss.sample_size} · {nss.response_rate_pct}% response
        </span>
      </div>
      <div className="space-y-2">
        {themes.map(([tk, t]) => (
          <div key={tk} data-testid={`nss-theme-${tk}`} className="flex items-center gap-3 text-xs">
            <span className="w-40 text-stone-700 shrink-0">{t.label}</span>
            <div className="flex-1 h-2 rounded-full bg-stone-100 overflow-hidden">
              <div
                className={`h-full rounded-full ${
                  t.score >= 80 ? "bg-emerald-500" :
                  t.score >= 70 ? "bg-emerald-400" :
                  t.score >= 60 ? "bg-amber-400"   : "bg-red-400"
                }`}
                style={{ width: `${t.score}%` }}
              />
            </div>
            <span className="font-mono text-stone-900 w-10 text-right">{t.score}%</span>
          </div>
        ))}
      </div>
      <div className="text-[10px] font-mono text-stone-400 mt-2">{HESA_ATTR}</div>
    </div>
  );
}

/* ─── Section 8 — Employment activity 15 months post-graduation ──────── */

export function EmploymentBreakdownBlock({ emp }) {
  if (!emp?.activity_pct) return null;
  const rows = [
    { key: "work",           label: "In work",             color: "bg-emerald-500" },
    { key: "work_and_study", label: "Working + studying",  color: "bg-emerald-400" },
    { key: "further_study",  label: "Further study only",  color: "bg-indigo-400" },
    { key: "other",          label: "Other / travel",      color: "bg-stone-300" },
    { key: "unemployed",     label: "Unemployed",          color: "bg-red-400" },
  ].filter(r => emp.activity_pct[r.key] != null);
  if (rows.length === 0) return null;
  return (
    <div data-testid="employment-breakdown" className="mt-5 rounded-lg border border-stone-200 bg-white p-4">
      <div className="label-uppercase text-stone-600 mb-3 flex items-center gap-1.5">
        <Briefcase className="h-3 w-3" /> What graduates do (15 months later)
        <span className="text-[9px] font-mono text-stone-400 ml-auto">
          n={emp.sample_size} · {emp.aggregation_year}
        </span>
      </div>
      {/* Stacked horizontal bar */}
      <div className="flex h-3 rounded-full overflow-hidden mb-3" data-testid="employment-stacked-bar">
        {rows.map(r => (
          <div key={r.key} className={r.color} style={{ width: `${emp.activity_pct[r.key]}%` }} title={`${r.label}: ${emp.activity_pct[r.key]}%`} />
        ))}
      </div>
      <div className="space-y-1.5 text-xs">
        {rows.map(r => (
          <div key={r.key} data-testid={`emp-row-${r.key}`} className="flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full ${r.color}`} />
            <span className="text-stone-700 flex-1">{r.label}</span>
            <span className="font-mono text-stone-900">{emp.activity_pct[r.key]}%</span>
          </div>
        ))}
      </div>
      <div className="text-[10px] font-mono text-stone-400 mt-2">{HESA_ATTR}</div>
    </div>
  );
}

/* ─── Section 7 — Location & campus ──────────────────────────────────── */

export function LocationBlock({ inst }) {
  if (!inst?.city && !inst?.postcode) return null;
  const mapsQ = encodeURIComponent([inst.full_address].filter(Boolean).join(" "));
  return (
    <Card data-testid="hesa-location" className="p-4 border-stone-200 mt-3">
      <div className="label-uppercase text-stone-600 mb-2 flex items-center gap-1.5">
        <MapPin className="h-3 w-3" /> Campus location
      </div>
      <div className="text-sm text-stone-800" data-testid="hesa-location-city">
        {inst.city}{inst.postcode && <span className="font-mono text-stone-500"> · {inst.postcode}</span>}
      </div>
      {inst.full_address && (
        <div className="text-xs text-stone-500 mt-1">{inst.full_address}</div>
      )}
      <div className="flex items-center gap-4 mt-2 text-[11px]">
        {mapsQ && (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${mapsQ}`}
            target="_blank" rel="noreferrer"
            data-testid="hesa-location-maps-link"
            className="inline-flex items-center gap-1 text-[#4C1D95] hover:underline"
          >
            View on Google Maps <ExternalLink className="h-3 w-3" />
          </a>
        )}
        {inst.provider_url && (
          <a
            href={inst.provider_url.startsWith("http") ? inst.provider_url : `https://${inst.provider_url}`}
            target="_blank" rel="noreferrer"
            data-testid="hesa-provider-link"
            className="inline-flex items-center gap-1 text-[#4C1D95] hover:underline"
          >
            University website <ExternalLink className="h-3 w-3" />
          </a>
        )}
        {inst.qaa_url && (
          <a
            href={inst.qaa_url}
            target="_blank" rel="noreferrer"
            data-testid="hesa-qaa-link"
            className="inline-flex items-center gap-1 text-[#4C1D95] hover:underline"
          >
            QAA quality report <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
      <div className="text-[10px] font-mono text-stone-400 mt-2">{HESA_ATTR}</div>
    </Card>
  );
}

/* ─── Course meta chips (honours, NHS-funded, sandwich, distance) ────── */

export function CourseMetaChips({ meta }) {
  if (!meta) return null;
  const chips = [
    meta.honours              && { label: "Honours degree",       tone: "emerald" },
    meta.nhs_funded           && { label: "NHS-funded",           tone: "indigo"  },
    meta.sandwich_placement   && { label: "Sandwich placement",   tone: "sky"     },
    meta.year_abroad_available&& { label: "Year abroad option",   tone: "amber"   },
    meta.distance_learning    && { label: "Distance learning",    tone: "stone"   },
  ].filter(Boolean);
  if (chips.length === 0) return null;
  const tone = { emerald: "border-emerald-300 bg-emerald-50 text-emerald-900",
                 indigo:  "border-indigo-300 bg-indigo-50 text-indigo-900",
                 sky:     "border-sky-300 bg-sky-50 text-sky-900",
                 amber:   "border-amber-300 bg-amber-50 text-amber-900",
                 stone:   "border-stone-300 bg-stone-50 text-stone-800" };
  return (
    <div data-testid="course-meta-chips" className="mt-3 flex flex-wrap gap-2">
      {chips.map((c, i) => (
        <span key={i} data-testid={`meta-chip-${i}`}
              className={`inline-flex items-center rounded-full border px-3 py-1 text-[11px] ${tone[c.tone]}`}>
          {c.label}
        </span>
      ))}
    </div>
  );
}
